import type { Dispatch, ReactElement, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";
import { Dropdown, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    COUNTRIES_POPUP_HEIGHT,
    COUNTRIES_POPUP_WIDTH,
    COUNTRY_LOADER_HEIGHT,
    COUNTRY_LOADER_WIDTH,
    DESCRIPTION_ID,
    MAX_COUNTRY_NAME_LENGTH,
    WORLD_WIDE_COUNTRY_ID,
} from "app/pages/activation/components/RankWidget/config";
import { CountryItem } from "app/pages/activation/components/RankWidget/CreationModal/Components/CountryDropdown/Item";
import { DropdownButton } from "app/pages/activation/components/RankWidget/CreationModal/Components/DropdownButton/DropdownButton";
import { getIsAvailableCountrySelected } from "app/pages/activation/components/RankWidget/helpers/getIsAvailableCountrySelected";
import { getIsMyCountryAvailable } from "app/pages/activation/components/RankWidget/helpers/getIsMyCountryAvailable";
import { modalsTexts } from "app/pages/activation/components/RankWidget/texts";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { RankTrackerCountry } from "app/pages/activation/types/api";
import CountryService from "app/services/CountryService";

import * as SC from "./styles";

type CountryDropdownSchema = {
    availableCountries: RankTrackerCountry[];
    country: number;
    setFormData?: Dispatch<SetStateAction<RTFormData>>;
    dataAutomation?: string;
    isLoading?: boolean;
};

export const CountryDropdown = ({
    availableCountries = [],
    country,
    setFormData,
    dataAutomation,
    isLoading = false,
}: CountryDropdownSchema) => {
    const [selectedCountry, setSelectedCountry] = useState(0);
    const isInitialWorldWide = country === WORLD_WIDE_COUNTRY_ID;
    const countryText = selectedCountry
        ? CountryService.getCountryById(selectedCountry)?.text
        : translate(modalsTexts.selectCountry);
    const isAvailableCountrySelected = getIsAvailableCountrySelected(availableCountries, selectedCountry);
    const isDisabled = availableCountries.length <= 1 || (isAvailableCountrySelected && !isInitialWorldWide);
    const isMyCountryAvailable = getIsMyCountryAvailable(availableCountries, country);
    const { rankTracker } = activationTracking;

    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isInitialWorldWide || !isMyCountryAvailable) {
            return;
        }

        setSelectedCountry(country);
    }, [country, availableCountries, isMyCountryAvailable, isAvailableCountrySelected, isInitialWorldWide]);

    useEffect(() => {
        setFormData?.((prevState) => ({ ...prevState, country: selectedCountry }));
    }, [selectedCountry, setFormData]);

    const onClose = () => dropdownRef?.current?.closePopup();

    const getItems = useMemo(
        () =>
            availableCountries.map(({ code, name }) => {
                const isTooltipEnabled = name.length > MAX_COUNTRY_NAME_LENGTH;

                return (
                    <CountryItem
                        key={name}
                        code={code}
                        name={name}
                        id={code.toString()}
                        isTooltipEnabled={isTooltipEnabled}
                        selectedCountry={selectedCountry}
                        setCode={setSelectedCountry}
                        onClose={onClose}
                    />
                );
            }),
        [availableCountries, selectedCountry],
    );

    const onClick = ({ code }: { code: number }) => {
        if (code) {
            setSelectedCountry(code);
        }
    };

    const onToggle = (isOpen: boolean) => {
        if (!isOpen) {
            rankTracker.trackChangeCountry(countryText);
        }
    };

    const searchFilter = (term: string, item: ReactElement): boolean => {
        return item.key.toLowerCase().includes(term.toLowerCase());
    };

    if (isLoading) {
        return (
            <PixelPlaceholderLoader
                height={COUNTRY_LOADER_HEIGHT}
                width={COUNTRY_LOADER_WIDTH}
            />
        );
    }

    return (
        <Dropdown
            ref={dropdownRef}
            width={COUNTRIES_POPUP_WIDTH}
            dropdownPopupHeight={COUNTRIES_POPUP_HEIGHT}
            appendTo={`#${DESCRIPTION_ID}`}
            dropdownPopupPlacement="bottom-left"
            onClick={onClick}
            closeOnItemClick
            buttonWidth="max-content"
            disabled={isDisabled}
            onToggle={onToggle}
            searchFilter={searchFilter}
            hasSearch
        >
            {[
                <DropdownButton
                    isDisabled={isDisabled}
                    dataAutomation={dataAutomation}
                    key="country-dropdown"
                >
                    <SC.CountryWrapper>
                        {selectedCountry ? (
                            <SWReactCountryIcons
                                countryCode={selectedCountry}
                                size="xs"
                            />
                        ) : null}

                        {countryText}
                    </SC.CountryWrapper>
                </DropdownButton>,
                ...getItems,
            ]}
        </Dropdown>
    );
};
